import { CreatorStatusFilterEnum, type CreatorsV2Filter } from '@metapic-apps/shared'
import { computed, type Ref, ref, watch } from 'vue'

import { useClientsList } from '@/api/clients.api'
import { useSearchAdvertiserGroupSegments } from '@/api/segments.api'
import { useGetUserTagListRequest } from '@/api/user-tags.api'
import { useGetUserListRequest, useGetUsersFromArrayRequest } from '@/api/users.api'
import { TargetItemEnum } from '@/lib/types/common.types'

export const useTargetOptions = (
  type: Ref<TargetItemEnum>,
  advertiserGroup: Ref<number>,
  query: Ref<string>,
  visibleEntities?: TargetItemEnum[],
) => {
  const clientQuery = ref('')
  const userQuery = ref('')
  const userTagQuery = ref('')
  const segmentQuery = ref('')
  const shouldSkipData = (entityType: TargetItemEnum) =>
    visibleEntities && !visibleEntities.includes(entityType)
  const { data: clientsData, isLoading: isClientsLoading } = useClientsList(
    () => ({ advertiserGroupId: advertiserGroup.value, query: clientQuery.value }),
    { skip: shouldSkipData(TargetItemEnum.Client) },
  )

  const userFilter = ref<CreatorsV2Filter>({
    advertiserGroup: { id: advertiserGroup.value, name: '' },
    query: userQuery.value,
    status: CreatorStatusFilterEnum.Accepted,
  })

  watch(
    () => [advertiserGroup.value, userQuery.value] as const,
    ([groupId, userQueryValue]) => {
      userFilter.value = {
        advertiserGroup: { id: groupId, name: '' },
        query: userQueryValue,
        status: CreatorStatusFilterEnum.Accepted,
      }
    },
    { immediate: true },
  )

  const { data: usersData, isLoading: isUsersLoading } = useGetUserListRequest(
    userFilter,
    shouldSkipData(TargetItemEnum.User),
  )

  const { data: userTagsData, isLoading: isUserTagsLoading } = useGetUserTagListRequest(
    userTagQuery,
    shouldSkipData(TargetItemEnum.UserTag),
  )

  const { data: segmentsData, isLoading: isSegmentsLoading } = useSearchAdvertiserGroupSegments(
    () => ({ advertiserGroupId: advertiserGroup.value, query: segmentQuery.value }),
    { skip: () => Boolean(shouldSkipData(TargetItemEnum.Segment)) },
  )

  const isLoading = computed(
    () =>
      isClientsLoading.value ||
      isUserTagsLoading.value ||
      isUsersLoading.value ||
      isSegmentsLoading.value,
  )
  const options = computed(() => {
    switch (type.value) {
      case TargetItemEnum.UserTag:
        return userTagsData.value.map((item) => ({
          label: item.name,
          id: item.id,
          type: TargetItemEnum.UserTag,
        }))
      case TargetItemEnum.Segment:
        return segmentsData.value.map((item) => ({
          label: item.name,
          id: item.id,
          type: TargetItemEnum.Segment,
        }))
      case TargetItemEnum.Client: {
        const result = []
        for (const item of clientsData.value) {
          result.push({ label: item.name, id: item.id, type: TargetItemEnum.Client })
          if (item.revenueTiers) {
            item.revenueTiers.forEach((revenueItem) =>
              result.push({
                label: `${item.name}: ${revenueItem.name}`,
                id: revenueItem.id,
                type: TargetItemEnum.RevenueTier,
                nestedItem: true,
              }),
            )
          }
        }
        return result
      }
      case TargetItemEnum.User:
      default:
        return usersData.value.map((item) => ({
          label: `[ID: ${item.id}] @${item.username} - ${item.email}`,
          id: item.id,
          type: TargetItemEnum.User,
        }))
    }
  })

  watch(
    () => query.value,
    (value) => {
      switch (type.value) {
        case TargetItemEnum.Client:
          return (clientQuery.value = value)
        case TargetItemEnum.User:
          return (userQuery.value = value)
        case TargetItemEnum.UserTag:
          return (userTagQuery.value = value)
        case TargetItemEnum.Segment:
          return (segmentQuery.value = value)
        default:
          return
      }
    },
  )

  return {
    options,
    isLoading,
    users: usersData,
  }
}

export const useTargetMatchUsers = (advertiserGroup: Ref<number>, users: Ref<string[]>) => {
  const data = ref<{
    matched: {
      label: string
      type: TargetItemEnum.User
      id: number
    }[]
    unmatched: string[]
  }>({ matched: [], unmatched: [] })
  const { data: requestResponse, isLoading } = useGetUsersFromArrayRequest(advertiserGroup, users)

  watch(
    () => requestResponse,
    () => {
      data.value = {
        matched: requestResponse.value.matched.map((item) => {
          return {
            label: `[ID: ${item.id}] @${item.username} - ${item.email}`,
            id: item.id,
            type: TargetItemEnum.User,
          }
        }),
        unmatched: requestResponse.value.unmatched,
      }
    },
    {
      deep: true,
    },
  )

  return {
    data,
    isLoading,
  }
}

type TargetItemEnumWithoutRevenueTier = Exclude<TargetItemEnum, TargetItemEnum.RevenueTier>
export const tabs: { key: TargetItemEnumWithoutRevenueTier; title: string }[] = [
  {
    key: TargetItemEnum.User,
    title: 'Creators',
  },
  {
    key: TargetItemEnum.UserTag,
    title: 'Tags',
  },
  {
    key: TargetItemEnum.Client,
    title: 'Clients',
  },
  {
    key: TargetItemEnum.Segment,
    title: 'Segments',
  },
]

export const targetInputPlaceholder: Record<TargetItemEnumWithoutRevenueTier, string> = {
  [TargetItemEnum.User]: 'Search creators by ID, email or username',
  [TargetItemEnum.UserTag]: 'Search for tags',
  [TargetItemEnum.Client]: 'Search for clients',
  [TargetItemEnum.Segment]: 'Search for segments',
}
